class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();

    // Non-blocking drop-down: close on a click/tap OUTSIDE the cart — but not when
    // it's the cart icon (which toggles) or an add-to-cart button (so the customer
    // can keep adding items without the cart getting in the way). Registered for BOTH
    // 'click' and 'pointerup': iOS Safari only synthesizes click for taps on
    // "clickable" elements, so taps on the plain page background never reached the
    // document click listener and the cart felt impossible to dismiss on iPhone.
    const closeOnOutside = (e) => {
      // pointerup is only for TOUCH/PEN (iOS taps on non-clickable areas never
      // synthesize click). Mouse users close via the native click event — acting on
      // mouse pointerup dismissed the cart when a text-selection drag ended outside.
      if (e.type === 'pointerup' && e.pointerType === 'mouse') return;
      if (!this.classList.contains('active')) return;
      if (
        e.target.closest('cart-drawer') ||
        e.target.closest('#cart-icon-bubble') ||
        e.target.closest('.product-form__submit, .quick-add__submit, .sticky-atc__button, [name="add"]')
      )
        return;
      this.close();
    };
    document.addEventListener('click', closeOnOutside);
    document.addEventListener('pointerup', closeOnOutside);
    // Escape closes from anywhere (there's no focus-trap on the non-blocking dropdown).
    document.addEventListener('keyup', (e) => {
      if (e.code === 'Escape' && this.classList.contains('active')) this.close();
    });
    // Keep the panel correctly anchored while it's open: re-run on viewport resize, and on
    // page scroll (rAF-throttled) so the panel flows with the header instead of being left
    // stuck at a stale, possibly off-screen position.
    window.addEventListener('resize', () => {
      if (this.classList.contains('active')) this.positionDropdown();
    });
    let scrollRaf = null;
    window.addEventListener(
      'scroll',
      () => {
        if (scrollRaf || !this.classList.contains('active')) return;
        scrollRaf = requestAnimationFrame(() => {
          scrollRaf = null;
          if (this.classList.contains('active')) this.positionDropdown();
        });
      },
      { passive: true }
    );

    // "Clear cart" button in the drawer header — empties the whole cart in one action.
    // Delegated on the cart-drawer element because the header is re-rendered on every
    // add/remove, so a directly-bound listener would be lost.
    this.addEventListener('click', (e) => {
      const clearBtn = e.target.closest('[data-cart-clear]');
      if (!clearBtn) return;
      e.preventDefault();
      this.clearCart(clearBtn);
    });
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.classList.contains('active') ? this.close() : this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.classList.contains('active') ? this.close() : this.open(cartLink);
      }
    });
  }

  // Position the panel in the empty gutter to the RIGHT of the (narrow, centered)
  // content column, so it sits BESIDE the products instead of on top of them. The
  // header cart icon sits at the content's right edge, so the gutter is the space to
  // its right. Falls back to near-full-width on small screens.
  positionDropdown() {
    // Position the STABLE panel element (#CartDrawer). It survives every re-render, so
    // these inline styles persist — adding/removing items never moves or re-sizes it.
    const panel = this.querySelector('#CartDrawer');
    if (!panel) return;
    const icon = document.querySelector('#cart-icon-bubble');
    // clientWidth excludes the scrollbar — fixed positioning measures from the content edge.
    const vw = document.documentElement.clientWidth || window.innerWidth;
    const vh = window.innerHeight;
    const edge = 16;
    const gap = 10;

    // ---- width + horizontal placement ----
    let cartW;
    if (vw < 750) {
      // small screens: near full-width panel, no content push
      cartW = vw - edge * 2;
      document.documentElement.style.setProperty('--cart-push', '0px');
    } else {
      // desktop: a comfortable panel width, and reserve exactly that much room on the
      // right (--cart-push) so the CSS shifts the page content left and products never
      // sit under the cart — no matter how narrow the window is.
      cartW = Math.min(384, Math.round(vw * 0.34));
      document.documentElement.style.setProperty('--cart-push', cartW + edge + 24 + 'px');
    }
    panel.style.left = 'auto';
    panel.style.right = edge + 'px';
    panel.style.setProperty('max-width', 'none', 'important');
    panel.style.setProperty('width', Math.round(cartW) + 'px', 'important');

    // ---- vertical placement (scroll- and sticky-header-proof) ----
    // Drop the panel just below the cart icon WHILE the icon is visible in the upper part
    // of the screen AND there's room for the panel beneath it; otherwise (the header — and
    // its icon — has scrolled off the top, is hidden, or sits too low) pin the panel near
    // the top of the viewport. Always CLAMP so the whole panel — top AND bottom, including
    // the checkout footer — stays on screen. This kills the old bug where a scrolled-away
    // icon gave a negative top so the panel sat jammed above the viewport and stayed stuck.
    // Re-run on scroll/resize so it flows with the header.
    // minH never demands more height than the viewport actually has (short/landscape screens).
    const minH = Math.min(240, Math.max(120, vh - edge * 2));
    let top = edge;
    if (icon) {
      const r = icon.getBoundingClientRect();
      // only anchor below the icon if it's visible up top AND leaves room for the panel —
      // otherwise pin to the top edge rather than a spot that overlaps the icon or overflows.
      if (r.bottom > 0 && r.bottom < vh * 0.6 && r.bottom + gap + minH <= vh - edge) {
        top = r.bottom + gap;
      }
    }
    top = Math.min(Math.max(edge, top), Math.max(edge, vh - minH - edge));
    // fill from `top` down to the bottom safe area; guarantee top + maxH <= vh - edge so the
    // footer/checkout is never clipped. The empty cart stays compact (short content anyway).
    let maxH = Math.max(minH, vh - top - edge);
    if (this.classList.contains('is-empty')) {
      maxH = Math.min(maxH, Math.round(Math.min(vh * 0.6, 400)));
    }
    panel.style.top = Math.round(top) + 'px';
    panel.style.setProperty('max-height', Math.round(maxH) + 'px', 'important');
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    this.positionDropdown();
    // Add the active class on a macrotask so the inline position writes above are
    // committed first — the opacity/transform transition on the stable #CartDrawer then
    // always runs cleanly. setTimeout, NOT requestAnimationFrame: rAF is paused in
    // hidden/backgrounded tabs so the drawer would never open there (known gotcha).
    setTimeout(() => {
      this.classList.add('animate', 'active');
      document.body.classList.add('cart-dropdown-open'); // pushes the page content left (CSS)
    });
    // Non-blocking drop-down: intentionally NO body scroll-lock and NO hard focus
    // trap, so the customer can keep browsing and adding without being interrupted.
  }

  close() {
    this.classList.remove('active');
    document.body.classList.remove('cart-dropdown-open');
    // No focus argument: the dropdown is non-modal, and passing this.activeElement
    // force-focused the header cart icon on EVERY close (even outside-clicks while
    // browsing), visibly scrolling the page back to the header.
    removeTrapFocus();
  }

  // Empty the entire cart. Uses a two-tap confirm so a stray tap can't wipe the cart:
  // the first tap arms the button ("Tap again to clear") for 3s; a second tap within that
  // window actually clears. Then it re-renders the drawer + header count from the server.
  clearCart(btn) {
    if (this._clearing) return;

    if (btn && btn.dataset.armed !== '1') {
      btn.dataset.armed = '1';
      btn.dataset.label = btn.textContent.trim();
      btn.textContent = btn.getAttribute('data-confirm-label') || 'Tap again to clear';
      btn.classList.add('is-armed');
      clearTimeout(this._clearTimer);
      this._clearTimer = setTimeout(() => {
        if (!btn.isConnected) return;
        btn.dataset.armed = '0';
        btn.textContent = btn.dataset.label || 'Clear cart';
        btn.classList.remove('is-armed');
      }, 3000);
      return;
    }

    clearTimeout(this._clearTimer);
    this._clearing = true;
    if (btn) {
      btn.setAttribute('disabled', '');
      btn.classList.remove('is-armed');
    }

    // Sweep the line items out one by one WHILE the network round-trip runs in
    // parallel — the wipe reads as deliberate and the latency hides behind it
    // (before this, mobile sat frozen for the round-trip then snapped to empty).
    const rows = Array.from(this.querySelectorAll('.cart-item'));
    const reducedMotion =
      window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const stepMs = reducedMotion ? 0 : rows.length > 8 ? 50 : 90;
    rows.forEach((row, i) => {
      row.style.setProperty('--vanish-delay', i * stepMs + 'ms');
      row.classList.add('cart-item--vanish');
    });
    const footer = this.querySelector('.drawer__footer');
    if (footer) footer.classList.add('cart-drawer__footer-fade');
    // lock the list so a stray tap can't queue a mutation mid-sweep
    const itemsWrap = this.querySelector('#CartDrawer-CartItems');
    if (itemsWrap) itemsWrap.classList.add('cart__items--disabled');
    // floor at 520ms so the footer fade (.4s + .1s delay) finishes even on 1-2 item carts
    const sweepMs = reducedMotion ? 0 : Math.max(rows.length ? (rows.length - 1) * stepMs + 340 : 0, 520);
    const sweepDone = new Promise((resolve) => setTimeout(resolve, sweepMs));

    const clearReq = () =>
      fetch('/cart/clear.js', { method: 'POST', headers: { 'Content-Type': 'application/json' } }).then((r) => {
        if (!r.ok) throw new Error('clear ' + r.status);
        return r;
      });

    const dataReady = clearReq()
      // A slow add-to-cart could land AFTER our clear and repopulate the server cart. Verify
      // it's actually empty and clear once more if not, so a stray item can't reach checkout.
      .then(() => fetch('/cart.js').then((r) => r.json()))
      .then((cart) => (cart && cart.item_count > 0 ? clearReq() : null))
      .then(() => fetch(`${window.location.pathname}?sections=cart-drawer,cart-icon-bubble`))
      .then((response) => {
        if (!response.ok) throw new Error('sections ' + response.status);
        return response.json();
      });

    Promise.all([dataReady, sweepDone])
      .then(([data]) => {
        if (data['cart-drawer']) {
          const parsed = new DOMParser()
            .parseFromString(data['cart-drawer'], 'text/html')
            .querySelector('#CartDrawer');
          if (parsed) document.querySelector('#CartDrawer').innerHTML = parsed.innerHTML;
        }
        if (data['cart-icon-bubble']) {
          const bubble = document.getElementById('cart-icon-bubble');
          const parsed = new DOMParser()
            .parseFromString(data['cart-icon-bubble'], 'text/html')
            .querySelector('.shopify-section');
          if (bubble && parsed) bubble.innerHTML = parsed.innerHTML;
        }
        this.classList.add('is-empty');
        // the empty state eases in instead of snapping (class self-removes)
        this.classList.add('just-cleared');
        setTimeout(() => this.classList.remove('just-cleared'), 650);
        const overlay = this.querySelector('#CartDrawer-Overlay');
        if (overlay) overlay.addEventListener('click', this.close.bind(this));
        this.positionDropdown();
        // The Clear button (which held keyboard focus) was just removed with the re-render —
        // re-home focus inside the drawer instead of letting it fall to <body>.
        if (this.classList.contains('active')) {
          const refocus = this.querySelector(
            '.drawer__inner-empty .button, .drawer__inner-empty .drawer__close, .drawer__inner .drawer__close'
          );
          if (refocus) {
            try {
              refocus.focus({ preventScroll: true });
            } catch (e) {
              refocus.focus();
            }
          }
        }
        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.cartUpdate) {
          publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-drawer-clear', cartData: { item_count: 0 } });
        }
      })
      .catch(() => {
        // Network / bad response: bring the swept rows BACK (they animated out
        // optimistically — without this a failed clear leaves an invisible cart)
        // and re-enable the button so the customer can retry. The reappear class
        // carries a transition so the restore eases instead of snapping.
        rows.forEach((row) => {
          if (!row.isConnected) return;
          row.classList.add('cart-item--reappear');
          row.classList.remove('cart-item--vanish');
          setTimeout(() => row.classList.remove('cart-item--reappear'), 500);
        });
        if (footer && footer.isConnected) footer.classList.remove('cart-drawer__footer-fade');
        if (itemsWrap && itemsWrap.isConnected) itemsWrap.classList.remove('cart__items--disabled');
        if (btn && btn.isConnected) {
          btn.removeAttribute('disabled');
          btn.dataset.armed = '0';
          btn.textContent = btn.dataset.label || 'Clear cart';
          btn.classList.remove('is-armed');
        }
      })
      .finally(() => {
        this._clearing = false;
      });
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    // The just-added item slides in instead of blinking into place. Target it
    // by the added line's key (adds that merge into an existing mid-list line
    // aren't the first row); fall back to the newest-first assumption.
    const arriving =
      (parsedState.key && this.querySelector(`.cart-item[data-line-key="${CSS.escape(parsedState.key)}"]`)) ||
      this.querySelector('.cart-item');
    if (arriving) {
      arriving.classList.add('cart-item--arrive');
      setTimeout(() => {
        if (arriving.isConnected) arriving.classList.remove('cart-item--arrive');
      }, 700);
    }

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

// ---- Cart motion module ----------------------------------------------------
// 1) Sweep a single line item out the moment its remove button is tapped — the
//    network change runs behind the slide (same motion language as the
//    clear-cart sweep). A failed change self-heals: the re-render restores
//    fresh row markup without the class.
// 2) FLIP the surviving rows: every cart re-render swaps the list DOM, which
//    made the rows below a removed item SNAP up. We snapshot row positions at
//    the moment of interaction, and after the swap each surviving row is
//    started from its old offset and eased to its new place.
(() => {
  const reduced = () =>
    window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  let snap = null; // { t, map: Map(rowKey -> top), scroll: Map(scroller -> scrollTop) }

  // Stable per-row identity: line key when the row carries one, else product
  // link — suffixed with an occurrence counter so duplicate-product lines
  // can't collide onto one map entry.
  const collectRows = (c) => {
    const seen = new Map();
    return Array.from(c.querySelectorAll('.cart-item')).map((row) => {
      let base = row.dataset.lineKey;
      if (!base) {
        const link = row.querySelector('a[href*="/products/"]');
        base = (link && link.getAttribute('href')) || row.id;
      }
      const n = (seen.get(base) || 0) + 1;
      seen.set(base, n);
      return { row, key: base + '::' + n };
    });
  };

  const containers = () => document.querySelectorAll('cart-drawer, cart-items');
  const scrollerOf = (c) => c.querySelector('.drawer__contents') || c;

  const takeSnapshot = () => {
    if (reduced()) return;
    const map = new Map();
    const scroll = new Map();
    containers().forEach((c) => {
      scroll.set(c, scrollerOf(c).scrollTop);
      collectRows(c).forEach(({ row, key }) => map.set(key, row.getBoundingClientRect().top));
    });
    snap = { t: Date.now(), map, scroll };
  };

  // Failed removes must not strand invisible rows: Dawn's error paths skip the
  // re-render, so a watchdog restores any row still connected after the round
  // trip should have finished — with an eased reappear, not a snap.
  const watchRemovedRow = (row) => {
    setTimeout(() => {
      if (!row.isConnected || !row.classList.contains('cart-item--vanish')) return;
      row.classList.add('cart-item--reappear');
      row.classList.remove('cart-item--vanish');
      setTimeout(() => row.classList.remove('cart-item--reappear'), 500);
    }, 4500);
  };

  // capture positions on any cart interaction that will trigger a re-render
  document.addEventListener(
    'click',
    (event) => {
      const t = event.target;
      if (!t.closest) return;
      const removeBtn = t.closest('cart-remove-button');
      const qtyBtn = t.closest('.cart-item .quantity__button, cart-drawer .quantity__button');
      if (!removeBtn && !qtyBtn) return;
      takeSnapshot();
      if (removeBtn) {
        const row = removeBtn.closest('.cart-item');
        if (row && !row.classList.contains('cart-item--vanish')) {
          row.style.setProperty('--vanish-delay', '0ms');
          row.classList.add('cart-item--vanish');
          watchRemovedRow(row);
        }
      }
    },
    true
  );
  document.addEventListener(
    'change',
    (event) => {
      if (event.target.closest && event.target.closest('.cart-item .quantity__input, cart-drawer .quantity__input')) {
        takeSnapshot();
      }
    },
    true
  );

  // Glide each surviving row from where it WAS. Runs SYNCHRONOUSLY inside the
  // MutationObserver callback — that's before the next paint, so there is no
  // flash-at-the-new-position frame (a debounce here caused a visible yank).
  const playFlip = () => {
    if (!snap || Date.now() - snap.t > 2500 || reduced()) {
      snap = null;
      return;
    }
    const prev = snap;
    snap = null;
    containers().forEach((c) => {
      // if the list was scrolled between snapshot and render, the stored tops
      // are meaningless — skip rather than fake-glide every row
      const priorScroll = prev.scroll.get(c);
      if (priorScroll !== undefined && Math.abs(scrollerOf(c).scrollTop - priorScroll) > 2) return;
      collectRows(c).forEach(({ row, key }) => {
        if (row.classList.contains('cart-item--vanish') || row.classList.contains('cart-item--arrive')) return;
        const oldTop = prev.map.get(key);
        if (oldTop === undefined) return;
        const dy = oldTop - row.getBoundingClientRect().top;
        if (Math.abs(dy) < 4) return;
        row.style.transition = 'none';
        row.style.transform = `translateY(${dy}px)`;
        void row.offsetWidth; /* commit the offset before easing back */
        row.style.transition = 'transform 0.38s cubic-bezier(0.16, 1, 0.3, 1)';
        row.style.transform = '';
        const cleanup = () => {
          row.style.transition = '';
        };
        row.addEventListener('transitionend', cleanup, { once: true });
        setTimeout(cleanup, 500); /* iOS backgrounding can swallow transitionend */
      });
    });
    // A last-item removal lands on the empty state with no clear-path ease —
    // give it the same gentle entrance.
    const drawer = document.querySelector('cart-drawer');
    if (
      drawer &&
      drawer.classList.contains('active') &&
      drawer.classList.contains('is-empty') &&
      !drawer.classList.contains('just-cleared')
    ) {
      drawer.classList.add('just-cleared');
      setTimeout(() => drawer.classList.remove('just-cleared'), 650);
    }
    // re-arm from the fresh layout so an overlapping second render still glides
    takeSnapshot();
  };

  const observer = new MutationObserver(playFlip);
  const startObserving = () => {
    containers().forEach((c) => observer.observe(c, { childList: true, subtree: true }));
  };
  if (document.readyState !== 'loading') startObserving();
  else document.addEventListener('DOMContentLoaded', startObserving);
})();

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
